/*
 * customs-declaration
 *
 * Every parcel leaves the EU, so before the cart reaches checkout the buyer
 * must state the goods, the value to declare, whether it is a sale or a gift,
 * and must accept the Terms & Conditions.
 *
 * The checkout button stays clickable on purpose. Greying it out until the
 * form was complete left the buyer with nothing else to press, and they went
 * looking for another way to pay instead. An incomplete form is caught on
 * submit, naming the missing fields in the buyer's own language.
 *
 * The answers are written to the cart as attributes, so they show up on the
 * order in the admin under "Additional details". The terms attribute carries
 * the version the buyer agreed to, which is what makes it usable as proof.
 */
if (!customElements.get('customs-declaration')) {
  customElements.define(
    'customs-declaration',
    class CustomsDeclaration extends HTMLElement {
      connectedCallback() {
        this.cartForm = document.getElementById('cart');

        this.goods = this.querySelector('#CustomsGoods');
        this.declaredValue = this.querySelector('#CustomsValue');
        this.nature = this.querySelectorAll('input[name="attributes[Customs: sale or gift]"]');
        this.terms = this.querySelector('#TermsAccepted');
        this.blocked = this.querySelector('[data-customs-blocked]');

        this.showErrors = false;
        this.saveTimer = null;

        this.addEventListener('input', this.onChange.bind(this));
        this.addEventListener('change', this.onChange.bind(this));
        this.addEventListener('focusout', this.onFocusOut.bind(this));

        if (this.cartForm) {
          /* The fields keep their `required` attribute as a fallback for when
             this script fails to load. While it is running we validate the
             form ourselves, so the browser's own bubbles do not pre-empt the
             localised messages. */
          this.cartForm.setAttribute('novalidate', 'novalidate');
          this.cartForm.addEventListener('submit', this.onSubmit.bind(this));
        }

        this.refresh();
      }

      collect() {
        const picked = Array.from(this.nature).find((input) => input.checked);
        return {
          goods: this.goods.value.trim(),
          value: this.declaredValue.value.trim(),
          nature: picked ? picked.value : '',
          terms: this.terms ? this.terms.checked : false,
        };
      }

      /*
       * Buyers type amounts in whatever convention they know: 1450, 1 450,
       * 1,450.00, 1.450,00. Spaces and apostrophes are dropped, then the last
       * separator decides: followed by exactly three digits and alone, it is a
       * thousands separator; otherwise it is the decimal point.
       */
      parseAmount(raw) {
        const cleaned = String(raw || '').replace(/[\s ']/g, '');
        if (!cleaned) return NaN;

        const lastComma = cleaned.lastIndexOf(',');
        const lastDot = cleaned.lastIndexOf('.');
        const lastSeparator = Math.max(lastComma, lastDot);
        if (lastSeparator === -1) return Number(cleaned);

        const trailingDigits = cleaned.length - lastSeparator - 1;
        if ((lastComma === -1 || lastDot === -1) && trailingDigits === 3) {
          return Number(cleaned.replace(/[.,]/g, ''));
        }

        const whole = cleaned.slice(0, lastSeparator).replace(/[.,]/g, '');
        return Number(`${whole}.${cleaned.slice(lastSeparator + 1)}`);
      }

      errors() {
        const data = this.collect();
        const found = [];
        if (!data.goods) found.push(['CustomsGoods', this.dataset.msgGoods]);
        if (!(this.parseAmount(data.value) > 0)) found.push(['CustomsValue', this.dataset.msgValue]);
        if (!data.nature) found.push(['CustomsNature', this.dataset.msgNature]);
        if (!data.terms) found.push(['TermsAccepted', this.dataset.msgTerms]);
        return found;
      }

      renderErrors(found) {
        const active = new Map(found);
        this.querySelectorAll('[data-error-for]').forEach((node) => {
          const message = this.showErrors ? active.get(node.dataset.errorFor) : undefined;
          node.textContent = message || '';
          node.hidden = !message;
        });
      }

      /* Nothing is disabled here: the messages only appear once the buyer has
         actually tried to check out. */
      refresh() {
        const found = this.errors();
        const valid = found.length === 0;

        this.renderErrors(found);
        if (this.blocked) this.blocked.hidden = valid || !this.showErrors;

        return valid;
      }

      onChange() {
        this.refresh();
        clearTimeout(this.saveTimer);
        this.saveTimer = setTimeout(() => this.save(), 500);
      }

      /* Rewrite the amount the buyer typed as a plain number, so what they see
         is exactly what gets declared. */
      onFocusOut(event) {
        if (event.target !== this.declaredValue) return;
        const amount = this.parseAmount(this.declaredValue.value);
        if (!(amount > 0)) return;

        const normalised = amount.toFixed(2);
        if (normalised === this.declaredValue.value) return;
        this.declaredValue.value = normalised;
        this.refresh();
        this.save();
      }

      onSubmit(event) {
        this.showErrors = true;
        if (this.refresh()) return;

        event.preventDefault();
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });

        const firstError = this.querySelector('.customs-decl__error:not([hidden])');
        const field = firstError ? firstError.closest('.customs-decl__field') : null;
        const focusable = field ? field.querySelector('input') : null;
        if (focusable) focusable.focus({ preventScroll: true });
      }

      save() {
        const data = this.collect();
        const body = JSON.stringify({
          attributes: {
            'Customs: goods declared': data.goods,
            'Customs: declared value (EUR)': data.value,
            'Customs: sale or gift': data.nature,
            'Terms accepted': data.terms && this.terms ? this.terms.value : '',
          },
        });
        const url = (window.routes && window.routes.cart_update_url) || '/cart/update.js';
        const config =
          typeof fetchConfig === 'function'
            ? fetchConfig()
            : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };

        fetch(url, { ...config, body }).catch(() => {});
      }
    }
  );
}
